import React, { useState, useEffect } from 'react';
import {
  Camera,
  Calendar as CalendarIcon,
  Clock,
  Box,
  Search,
  MapPin,
  Filter,
  Check,
  X,
  Camera as CameraIconAlt,
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight, FileText, Users, Activity, CheckCircle, XCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import Map, { Marker, NavigationControl, Popup } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Youtube, Twitch } from 'lucide-react';
import ProjectDetailsModal from '../components/requirements/ProjectDetailsModal';
import { getProject as getProjectDetails } from '../lib/api/requirementsApi';
import { cn } from '../lib/utils';
import { format } from 'date-fns';
import {
  getDashboardCount,
  getCrewAvailability,
  GetUpcomingShoots,
  getPendingProjects,
  acceptOrDeclineProject,
  getDashboardDetail,
  getRecentActivity
} from '../services/creatorService';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const legendItems = [
  { label: 'Active Events', count: 2, color: 'bg-teal-500' },
  { label: 'Upcoming Events', count: 0, color: 'bg-blue-800' },
  { label: 'Pending Request', count: 0, color: 'bg-yellow-400' },
  { label: 'Assigned Crew', count: 0, color: 'bg-orange-400' },
  { label: 'Equipment Request', count: 0, color: 'bg-blue-400' }
];

const CreatorDashboard = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [acceptShootEvent, setAcceptShootEvent] = useState(null);
  const [declineEquipmentItem, setDeclineEquipmentItem] = useState(null);
  const [projectDetailsOpen, setProjectDetailsOpen] = useState(false);
  const [projectDetailsData, setProjectDetailsData] = useState(null);
  const [projectDetailsLoading, setProjectDetailsLoading] = useState(false);
  const [recentActivity, setRecentActivity] = useState([]);
  const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

  // API data states
  const [crewMemberId, setCrewMemberId] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [dashboardDetail, setDashboardDetail] = useState(null);
  const [mapMarkers, setMapMarkers] = useState([]);
  const [upcomingProjects, setUpcomingProjects] = useState([]);
  const [pendingProjects, setPendingProjects] = useState([]);
  const [availabilityData, setAvailabilityData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userName, setUserName] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [customRange, setCustomRange] = useState({ from: undefined, to: undefined });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [shootsSearchQuery, setShootsSearchQuery] = useState("");
  const [requestsSearchQuery, setRequestsSearchQuery] = useState("");
  const [noShootsText, setNoShootsText] = useState("");

  // Initial view state centered on US
  const [viewState, setViewState] = useState({
    latitude: 39.8283,
    longitude: -98.5795,
    zoom: 3.5,
    pitch: 0,
    bearing: 0
  });

  // Geocode address helper
  const geocodeAddress = async (address) => {
    if (!address || !MAPBOX_TOKEN) return null;
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${MAPBOX_TOKEN}&country=us&proximity=-98.5795,39.8283&limit=1&types=place,locality,region`
      );
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].center;
        return { lat, lng };
      }
    } catch (error) {
      console.error("Geocoding error for", address, ":", error);
    }
    return null;
  };

  const formatDisplayLocation = (location) => {
    if (!location || location === 'Location TBD') return 'Location TBD';

    let addressStr = location;

    try {
      if (typeof location === 'string' && (location.trim().startsWith('{') || location.includes('"address"'))) {
        const locObj = JSON.parse(location);
        addressStr = locObj.address || addressStr;
      } else if (typeof location === 'object' && location !== null) {
        addressStr = location.address || addressStr;
      }
    } catch (e) {
      console.error("Error parsing location JSON:", e);
    }

    if (addressStr.includes(',')) {
      const parts = addressStr.split(',').map(p => p.trim());
      if (parts.length >= 3) {
        const city = parts[1];
        const state = parts[2].replace(/[0-9]/g, '').trim();
        return `${city}, ${state}`;
      }
      return parts[0];
    }

    return addressStr;
  };

  const getIcon = (iconType) => {
    switch (iconType) {
      case "FileText":
        return <FileText className="text-gray-500 dark:text-gray-300" />;
      case "Users":
        return <Users className="text-gray-500 dark:text-gray-300" />;
      case "Activity":
        return <Activity className="text-gray-500 dark:text-gray-300" />;
      case "XCircle":
        return <XCircle className="text-gray-500 dark:text-gray-300" />;
      default:
        return <Activity className="text-gray-500 dark:text-gray-300" />;
    }
  };

  const fetchDashboardData = async (activeFilters = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const payload = {};

      // 1. Handle Search
      if (activeFilters.search) payload.search = activeFilters.search;

      // 2. Handle Status (Only send if not "all")
      if (activeFilters.status && activeFilters.status !== "all") {
        payload.status = activeFilters.status;
      }

      // 3. Handle Date Filter (Only send if not "all")
      if (activeFilters.date_filter && activeFilters.date_filter !== "all") {
        payload.date_filter = activeFilters.date_filter;

        if (activeFilters.date_filter === 'custom' && customRange?.from && customRange?.to) {
          payload.start_date = customRange.from.toISOString().split('T')[0];
          payload.end_date = customRange.to.toISOString().split('T')[0];
        }
      }

      // Call API with the constructed payload
      const detailResponse = await getDashboardDetail(payload);

      // 2. Fetch Stats (Always required)
      const statsResponse = await getDashboardCount();
      if (statsResponse.success) setDashboardStats(statsResponse.data);

      // 3. Fetch Dashboard Details with Payload
      // const detailResponse = await getDashboardDetail(payload);

      if (detailResponse.success) {
        setDashboardDetail(detailResponse.data);

        // Process Map Markers (Existing Logic)
        if (detailResponse.data?.allShoots) {
          const activeShoots = detailResponse.data.allShoots;
          const shootsToGeocode = activeShoots.filter(shoot =>
            shoot.project?.event_location && shoot.project?.event_location !== 'Location TBD'
          );

          const geocodePromises = shootsToGeocode.map(async (shoot) => {
            const coords = await geocodeAddress(shoot.project.event_location);
            if (coords) {
              return {
                id: shoot.project.stream_project_booking_id,
                lat: coords.lat,
                lng: coords.lng,
                status: 'active',
                title: shoot.project_name
              };
            }
            return null;
          });
          const markers = (await Promise.all(geocodePromises)).filter(Boolean);
          setMapMarkers(markers);

          // Set Upcoming Shoots
          const formattedUpcoming = detailResponse.data.allShoots.map(shoot => ({
            ...shoot,
            display_location: formatDisplayLocation(shoot.event_location || shoot.project?.event_location)
          }));
          setUpcomingProjects(formattedUpcoming);
        }

        // Process Pending Requests (Existing Logic)
        if (detailResponse.data?.pendingRequests) {
          const transformedPending = detailResponse.data.pendingRequests.map(item => {
            const rawLocation = item.project?.event_location || item.event_location;
            return {
              ...item,
              ...item.project,
              event_location: rawLocation,
              display_location: formatDisplayLocation(rawLocation),
              project_id: item.project?.stream_project_booking_id || item.project_id
            };
          });
          setPendingProjects(transformedPending);
        }
      }

      // 4. Fetch Availability (Existing Logic)
      const currentDate = new Date();
      const availabilityResponse = await getCrewAvailability({
        month: currentDate.getMonth() + 1,
        year: currentDate.getFullYear()
      });
      if (availabilityResponse.success) {
        setAvailabilityData(availabilityResponse.data?.availability || null);
      }

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };


  const filteredUpcomingProjects = upcomingProjects.filter((item) => {
    const query = shootsSearchQuery.toLowerCase();
    const projectName = item.project?.project_name?.toLowerCase() || "";
    const location = item.display_location?.toLowerCase() || "";

    return projectName.includes(query) || location.includes(query);
  });

  // Local filtering for Shoot Requests
  const filteredPendingProjects = pendingProjects.filter((item) => {
    const query = requestsSearchQuery.toLowerCase();
    const projectName = item.project_name?.toLowerCase() || "";
    const location = item.display_location?.toLowerCase() || "";

    return projectName.includes(query) || location.includes(query);
  });

  // Optional: Filter Map Markers locally too
  const filteredMapMarkers = mapMarkers.filter(marker =>
    marker.title?.toLowerCase().includes(shootsSearchQuery.toLowerCase())
  );

  useEffect(() => {
    const statusLabels = {
      all: "shoots scheduled",
      active: "active shoots scheduled",
      completed: "completed shoots recorded",
      cancelled: "cancelled shoots recorded"
    };

    const dateLabels = {
      all: "for all time",
      today: "today",
      upcoming: "for upcoming dates",
      this_week: "this week",
      this_month: "this month",
      custom: "for the selected range"
    };

    if (filteredUpcomingProjects.length === 0) {
      const statusPart = statusLabels[statusFilter.toLowerCase()] || "shoots";
      const datePart = dateLabels[dateFilter.toLowerCase()] || "";

      const message = `No ${statusPart} ${datePart}`.trim();

      setNoShootsText(message);
    } else {
      setNoShootsText("");
    }

  }, [filteredUpcomingProjects, statusFilter, dateFilter]);


  useEffect(() => {
    const fetchRecentActivity = async () => {
      setIsLoading(true);
      try {
        const response = await getRecentActivity();
        if (response.success && response.data) {
          setRecentActivity(response.data);
        } else {
          setError('Failed to load recent activity');
        }
      } catch (err) {
        setError('An error occurred while fetching recent activity');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecentActivity();
  }, []);


  useEffect(() => {
    fetchDashboardData({ status: "all", date_filter: "all" });
  }, []);

  useEffect(() => {
    if (dateFilter !== 'custom') {
      fetchDashboardData({
        status: statusFilter,
        date_filter: dateFilter,
      });
    }
  }, [statusFilter, dateFilter]);

  const handleApplyCustomDate = () => {
    if (customRange.from && customRange.to) {
      setIsDatePickerOpen(false);
      fetchDashboardData({ date_filter: 'custom' });
    } else {
      toast.error("Please select both start and end dates");
    }
  };

  useEffect(() => {
    const fetchAvailabilityData = async (year, month) => {
      try {
        setIsLoading(true);
        const availabilityResponse = await getCrewAvailability({
          month: month + 1,
          year: year,
        });
        if (availabilityResponse.success) {
          const availability = availabilityResponse.data?.availability || {};

          const updatedAvailability = Object.keys(availability).reduce((acc, date) => {
            const dayData = availability[date];
            if (dayData.projectAssigned) {
              acc[date] = {
                ...dayData,
                available: true,
              };
            } else {
              acc[date] = dayData;
            }
            return acc;
          }, {});

          setAvailabilityData(updatedAvailability);
        } else {
          console.error("Failed to fetch availability:", availabilityResponse.error);
        }
      } catch (err) {
        console.error("Error fetching availability data:", err);
        setError("Failed to load availability data");
      } finally {
        setIsLoading(false);
      }
    };
    fetchAvailabilityData(date.getFullYear(), date.getMonth());
  }, [date]);

  const handlePreviousMonth = () => {
    const newDate = new Date(date);
    newDate.setMonth(newDate.getMonth() - 1);
    setDate(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(date);
    newDate.setMonth(newDate.getMonth() + 1);
    setDate(newDate);
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    const name = user?.name;
    console.log("Logged in user:", name);

    if (name) {
      setUserName(name);
    }
  }, []);

  useEffect(() => {

    const fetchDashboardData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch dashboard statistics
        const statsResponse = await getDashboardCount();
        if (statsResponse.success) {
          setDashboardStats(statsResponse.data);
        } else {
          console.error('Failed to fetch dashboard stats:', statsResponse.error);
        }

        // Fetch dashboard details
        const detailResponse = await getDashboardDetail(crewMemberId);
        if (detailResponse.success) {
          setDashboardDetail(detailResponse.data);

          // Process Map Markers from Active Shoots
          if (detailResponse.data?.allShoots) {
            const activeShoots = detailResponse.data.allShoots;

            // Filter shoots that have a location string
            const shootsToGeocode = activeShoots.filter(shoot => shoot.project.event_location && shoot.project.event_location !== 'Location TBD');

            const geocodePromises = shootsToGeocode.map(async (shoot) => {
              const coords = await geocodeAddress(shoot.project.event_location);
              if (coords) {
                return {
                  id: shoot.project.stream_project_booking_id,
                  lat: coords.lat,
                  lng: coords.lng,
                  status: 'active',
                  title: shoot.project_name
                };
              }
              return null;
            });

            const markers = (await Promise.all(geocodePromises)).filter(Boolean);
            setMapMarkers(markers);
          }

          // Set Upcoming Shoots
          if (detailResponse.data?.allShoots) {
            const formattedUpcoming = detailResponse.data.allShoots.map(shoot => ({
              ...shoot,
              display_location: formatDisplayLocation(shoot.event_location)
            }));
            setUpcomingProjects(formattedUpcoming);
          }


          if (detailResponse.data?.pendingRequests) {
            const transformedPending = detailResponse.data.pendingRequests.map(item => {
              const rawLocation = item.project?.event_location || item.event_location;

              return {
                ...item,
                ...item.project,
                event_location: rawLocation, // keep raw for map/details
                display_location: formatDisplayLocation(rawLocation), // cleaned for card
                project_id: item.project?.stream_project_booking_id || item.project_id
              };
            });
            setPendingProjects(transformedPending);
          }

        } else {
          console.error('Failed to fetch dashboard detail:', detailResponse.error);
        }

        // Separate calls removed as data is now provided by dashboard-detail

        // Fetch availability calendar data for current month
        const currentDate = new Date();
        const availabilityResponse = await getCrewAvailability({
          month: currentDate.getMonth() + 1,
          year: currentDate.getFullYear()
        });
        if (availabilityResponse.success) {
          setAvailabilityData(availabilityResponse.data?.availability || null);
        } else {
          console.error('Failed to fetch availability:', availabilityResponse.error);
        }

      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);
  // Handle accept/decline project
  const handleAcceptProject = async (projectId, status) => {
    try {
      // status 1 = accept, 2 = decline
      const response = await acceptOrDeclineProject(projectId, status);

      if (response.success) {
        const detailResponse = await getDashboardDetail();
        if (detailResponse.success) {
          setUpcomingProjects(detailResponse.data?.allShoots || []);

          const transformedPending = (detailResponse.data?.pendingRequests || []).map(item => ({
            ...item,
            ...item.project,
            event_location: item.project?.event_location || item.event_location,
            project_id: item.project?.stream_project_booking_id || item.project_id
          }));
          setPendingProjects(transformedPending);
        }

        if (status === 1) {
          toast.custom((t) => (
            <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5 p-4 relative`}>
              <div className="flex-shrink-0 pt-0.5">
                <CheckCircle2 className="h-6 w-6 text-green-500" />
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-bold text-gray-900">Shoot request accepted</p>
                <p className="mt-1 text-sm text-gray-500">You've successfully accepted the shoot. Check your calendar for details.</p>
              </div>
              <button onClick={() => toast.dismiss(t.id)} className="absolute top-4 right-4 text-gray-400"><X className="h-5 w-5" /></button>
            </div>
          ));
        } else {
          toast.error('Shoot request declined');
        }

        setAcceptShootEvent(null);
      } else {
        toast.error(response.error || 'Failed to process request');
      }
    } catch (err) {
      console.error('Error in handleAcceptProject:', err);
      toast.error('An error occurred. Please try again.');
    }
  };
  const handleOpenProjectDetails = async (projectId) => {
    if (!projectId) return;
    try {
      setProjectDetailsLoading(true);
      const response = await getProjectDetails(projectId);
      if (response && response.data) {
        setProjectDetailsData(response.data);
        setProjectDetailsOpen(true);
      } else {
        toast.error('Failed to load project details');
      }
    } catch (err) {
      console.error('Error loading project details:', err);
      toast.error('Failed to load project details');
    } finally {
      setProjectDetailsLoading(false);
    }
  };

  const statsData = [
    {
      title: 'Completed Shoots',
      value: String(dashboardStats?.completedShoots ?? 0),
      icon: Camera,
    },
    {
      title: 'Upcoming Shoots',
      value: String(dashboardStats?.upcomingShoots ?? 0),
      icon: CalendarIcon,
    },
    {
      title: 'Pending Requests',
      value: String(dashboardStats?.pendingRequests ?? 0),
      icon: Clock,
    },
    {
      title: 'Equipment Requests',
      value: String(dashboardStats?.equipmentRequests ?? 0),
      icon: Box,
    }
  ];


  const cardHoverClass =
    "bg-white px-5 py-3 lg:p-5 rounded-2xl border border-[#E8DDD3] shadow-sm " +
    "transition-all duration-300 ease-in-out " +
    "hover:border-[#e8d0aa] hover:shadow-[5px_5px_0px_0px_#e8d0aa] hover:-translate-y-1 " +
    "hover:scale-[1.00] cursor-pointer relative overflow-hidden";


  const EventDetailsModal = ({ event, isOpen, onClose }) => {
    if (!event) return null;

    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="fixed right-0 top-0 z-50 h-full w-[400px] gap-0 border-l bg-white dark:bg-card p-0 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right left-auto translate-x-0 translate-y-0 sm:max-w-[450px] sm:rounded-none">
          <ScrollArea className="h-full p-6">
            <div className="flex justify-between items-start mb-6 pt-2">
              <span className="bg-emerald-500 text-white px-3 py-1 rounded-sm text-xs font-bold uppercase tracking-wider">Active</span>
              {/* Close button is handled by DialogPrimitive.Close in DialogContent but we can ensure spacing */}
            </div>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-foreground mb-4">{event.title}</h2>

            <div className="flex flex-wrap gap-4 text-sm text-gray-400 mb-6">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4" /> {event.date}
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" /> {event.location}
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" /> {event.time}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-sm font-medium text-gray-500 dark:text-muted-foreground mb-3 block">Streaming Platforms</h3>
              <div className="flex gap-3">
                <Button variant="outline" className="gap-2 bg-white dark:bg-background hover:bg-gray-50 dark:hover:bg-gray-800 border-gray-100 dark:border-border">
                  <Youtube className="h-4 w-4 text-red-600" /> YouTube
                </Button>
                <Button variant="outline" className="gap-2 bg-white dark:bg-background hover:bg-gray-50 dark:hover:bg-gray-800 border-gray-100 dark:border-border">
                  <Twitch className="h-4 w-4 text-purple-600" /> Twitch
                </Button>
                <div className="h-10 px-3 flex items-center justify-center border dark:border-border rounded-md text-sm font-medium text-gray-400 bg-white dark:bg-background">
                  +4
                </div>
              </div>
            </div>

            <div className="mb-8 p-6 bg-[#F5F1EE] dark:bg-secondary/10 rounded-xl border border-[#E8DDD3] dark:border-border">
              <h3 className="font-bold text-gray-900 dark:text-foreground mb-4">Assigned Crew (10/10)</h3>
              <div className="flex gap-4 overflow-x-auto pb-2 no-scrollbar">
                {[1, 2].map(i => (
                  <div key={i} className="flex items-center gap-3 bg-white dark:bg-card p-3 rounded-xl border border-[#E8DDD3] dark:border-border min-w-[200px]">
                    <img src={`https://i.pravatar.cc/100?img=${i + 15}`} className="w-10 h-10 rounded-full object-cover" alt="crew" />
                    <div>
                      <p className="font-bold text-sm text-gray-900 dark:text-foreground">Michael Chen</p>
                      <p className="text-xs text-gray-500 dark:text-muted-foreground">Camera Operator</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8 p-6 bg-[#F5F1EE] dark:bg-secondary/10 rounded-xl border border-[#E8DDD3] dark:border-border">
              <h3 className="font-bold text-gray-900 dark:text-foreground mb-4">Assigned Equipment (10/10)</h3>
              <div className="flex gap-4 overflow-x-auto pb-2 no-scrollbar">
                {[1, 2].map(i => (
                  <div key={i} className="flex items-center gap-3 bg-white dark:bg-card p-3 rounded-xl border border-gray-100 dark:border-border min-w-[200px]">
                    <div className="w-10 h-10 bg-gray-50 dark:bg-background rounded-lg flex items-center justify-center">
                      <CameraIconAlt className="h-5 w-5 text-gray-800 dark:text-foreground" />
                    </div>
                    <div>
                      <p className="font-bold text-sm text-gray-900 dark:text-foreground">Camera</p>
                      <p className="text-xs text-gray-500 dark:text-muted-foreground w-32 truncate">Sony FX6 Cinema Camera Kit</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8 p-6 bg-[#F5F1EE] dark:bg-secondary/10 rounded-xl border border-[#E8DDD3] dark:border-border">
              <h3 className="font-bold text-gray-900 dark:text-foreground mb-4">Time & Budget</h3>
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <p className="text-gray-500 dark:text-muted-foreground text-xs mb-1">Duration (hours)</p>
                  <p className="font-bold text-gray-900 dark:text-foreground text-lg">8 Hours</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-muted-foreground text-xs mb-1">Budget ($)</p>
                  <p className="font-bold text-gray-900 dark:text-foreground text-lg">$350</p>
                </div>
              </div>
            </div>

            <div className="mb-8 p-6 bg-[#F5F1EE] dark:bg-secondary/10 rounded-xl border border-[#E8DDD3] dark:border-border">
              <h3 className="font-bold text-gray-900 dark:text-foreground mb-4">Equipment Needed</h3>
              <div className="flex flex-wrap gap-2">
                {['Tripod', 'Audio Mixer', 'Green Screen', 'Light Diffuser', 'Boom Microphone'].map((tag, i) => (
                  <span key={i} className="px-3 py-1 bg-white dark:bg-card border border-gray-200 dark:border-border rounded-md text-sm text-gray-700 dark:text-foreground font-medium shadow-sm">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-2 p-6 bg-white dark:bg-card rounded-xl border border-[#E8DDD3] dark:border-border">
              <h3 className="font-bold text-gray-900 dark:text-foreground mb-4">Contact Information</h3>
              <label className="text-sm font-bold text-gray-900 dark:text-foreground mb-2 block">Main Contact Name</label>
              <Input value="Jay Pawar" readOnly className="bg-white dark:bg-background border-[#E8DDD3] dark:border-border text-gray-900 dark:text-foreground" />
            </div>

          </ScrollArea>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className="p-4 md:p-8 bg-[#F5F1EE] dark:bg-background min-h-screen font-sans">
      {/* Header */}
      <div className="mb-4 lg:mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-foreground">
          Welcome back, {userName || "Creative Pro"}
        </h1>
        <p className="text-sm md:text-base text-gray-500 dark:text-muted-foreground mt-1">
          Here's what's happening with your shoots and requests today.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-6 mb-4 lg:mb-8">
        {statsData.map((stat, index) => (
          <div
            key={index}
            className="relative bg-white dark:bg-card p-4 md:p-6 rounded-xl shadow-sm border border-[#E8DDD3] dark:border-border flex flex-col justify-between h-38 overflow-hidden group transition-all duration-300 hover:shadow-xl hover:border-[#B8A697] dark:hover:border-primary/50"
          >
            {/* SOFT AMBIENT LIGHT (INSIDE CARD) */}
            <div className="ambient-light" />

            {/* CONTENT */}
            <div className="relative z-10 flex justify-between items-start">
              <div className="pr-12">
                <p className="text-gray-500 dark:text-muted-foreground text-sm font-medium transition-all duration-300 group-hover:text-base group-hover:leading-none group-hover:font-semibold leading-none h-7 flex items-center">
                  {stat.title}
                </p>
                <h3 className="text-4xl font-bold text-gray-900 dark:text-foreground mt-2">
                  {stat.value}
                </h3>
              </div>

              <div className="hidden md:block p-3 rounded-xl bg-[#E8DDD3] dark:bg-secondary/20 transition-all duration-300 group-hover:bg-[#B8A697] dark:group-hover:bg-primary/20 group-hover:scale-110">
                <stat.icon className="h-6 w-6 text-gray-700 dark:text-foreground transition-colors duration-300 group-hover:text-white dark:group-hover:text-primary" />
              </div>
            </div>

            {/* <div className="relative z-10 flex items-center mt-auto">
              <span className={`text-sm font-bold ${stat.trend === "up" ? "text-green-600" : "text-red-500"}`}>
                {stat.change}
              </span>
              <span className="text-gray-400 dark:text-muted-foreground text-sm ml-2">
                {stat.period}
              </span>
            </div> */}
            <div className="relative z-10 flex items-center mt-auto">
              {stat.change && (
                <span
                  className={`text-sm font-bold ${
                    stat.trend === "up" ? "text-green-600" : "text-red-500"
                  }`}
                >
                  {stat.change}
                </span>
              )}
              {stat.period && (
                <span className="text-gray-400 dark:text-muted-foreground text-sm ml-2">
                  {stat.period}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Grid: Map & Calendar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-8 mb-4 lg:mb-8">
        {/* Map Section */}
        <div className="lg:col-span-2 bg-white dark:bg-card rounded-2xl shadow-sm border border-[#E8DDD3] dark:border-border overflow-hidden relative min-h-[500px]">
          <div className="absolute top-4 left-4 z-10 flex h-10 w-48 items-center rounded-md border border-[#E8DDD3] bg-white px-3 shadow-sm dark:border-border dark:bg-background">
            <Search className="h-4 w-4 shrink-0 text-gray-400 dark:text-muted-foreground" />
            <input
              type="text"
              placeholder="Search events..."
              className="ml-2 w-full bg-transparent text-sm outline-none text-gray-900 dark:text-foreground placeholder:text-gray-400"
            />
          </div>

          {/* Select Box Container */}
          <div className="absolute top-16 right-4 z-10 md:top-4 md:left-auto">
            <Select defaultValue="active">
              <SelectTrigger className="h-10 w-[160px] rounded-md border-[#E8DDD3] bg-white text-gray-900 shadow-sm dark:border-border dark:bg-background dark:text-foreground focus:ring-0">
                <SelectValue placeholder="Active events" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-card border-gray-100 dark:border-border">
                <SelectItem value="active">Active events</SelectItem>
                <SelectItem value="all">All events</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Map */}
          <div className="w-full h-full bg-gray-100">
            {MAPBOX_TOKEN ? (
              <Map
                {...viewState}
                onMove={(evt) => setViewState(evt.viewState)}
                style={{ width: "100%", height: "100%" }}
                mapStyle={
                  theme === "dark"
                    ? "mapbox://styles/mapbox/dark-v11"
                    : "mapbox://styles/mapbox/light-v11"
                }
                mapboxAccessToken={MAPBOX_TOKEN}
              >
                {mapMarkers.map((marker) => (
                  <Marker
                    key={marker.id}
                    latitude={marker.lat}
                    longitude={marker.lng}
                    anchor="center"
                  >
                    <div className="relative flex items-center justify-center w-12 h-12 group cursor-pointer hover:scale-110 transition-transform duration-200">
                      {/* Outer Glow/Shadow */}
                      <div className="absolute inset-0 bg-emerald-500/10 rounded-full blur-md group-hover:bg-emerald-500/20" />

                      {/* Dashed Outer Ring */}
                      <div className="absolute inset-2 rounded-full border-[2.5px] border-dashed border-gray-900/80" />

                      {/* Background for contrast */}
                      <div className="absolute inset-[10px] bg-emerald-900/10 rounded-full" />

                      {/* Inner Dot */}
                      <div
                        className={`
                        relative w-3.5 h-3.5 rounded-full shadow-sm z-10
                        ${
                          marker.status === "active"
                            ? "bg-[#00DDA3] shadow-[0_0_8px_#00DDA3]"
                            : "bg-blue-500"
                        }
                      `}
                      />
                    </div>
                  </Marker>
                ))}
              </Map>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-secondary/10 text-gray-400 dark:text-muted-foreground">
                Map Token Missing or Invalid
              </div>
            )}
          </div>

          {/* Status Legend */}
          <div className="absolute bottom-4 left-4 bg-white dark:bg-card p-4 rounded-xl shadow-lg border border-[#E8DDD3] dark:border-border z-10 w-64">
            <h4 className="font-bold text-gray-900 dark:text-foreground mb-3 text-base">
              Status Legend
            </h4>
            <div className="space-y-2">
              {legendItems.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between text-sm"
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-sm ${item.color}`}></span>
                    <span className="text-gray-600 dark:text-muted-foreground">
                      {item.label}
                    </span>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-foreground">
                    {item.count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Availability Calendar */}
        <div className="bg-white dark:bg-card p-6 rounded-2xl shadow-sm border border-[#E8DDD3] dark:border-border flex flex-col h-full">
          <div className="flex items-center gap-2 mb-4">
            <CalendarIcon className="h-5 w-5 text-gray-700 dark:text-foreground" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-foreground">
              Availability
            </h3>
          </div>

          <div className="flex-1 flex flex-col items-center">
            {/* Custom Calendar */}
            <div className="w-full">
              {/* Calendar Header */}
              <div className="flex items-center justify-between mb-4">
                <button
                  className="w-8 h-8 flex items-center justify-center rounded-xl bg-[#F4F0ED] dark:bg-secondary/20 border border-[#E8DDD3] dark:border-border text-gray-700 dark:text-foreground transition-colors shadow-sm hover:bg-[#C4B5A3] dark:hover:bg-primary/50 hover:text-white dark:hover:text-primary"
                  onClick={handlePreviousMonth} // Trigger previous month
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <h4 className="text-base font-semibold text-gray-900 dark:text-foreground">
                  {date.toLocaleString("default", {
                    month: "long",
                    year: "numeric",
                  })}
                </h4>
                <button
                  className="w-8 h-8 flex items-center justify-center rounded-xl bg-[#F4F0ED] dark:bg-secondary/20 border border-[#E8DDD3] dark:border-border text-gray-700 dark:text-foreground transition-colors shadow-sm hover:bg-[#C4B5A3] dark:hover:bg-primary/50 hover:text-white dark:hover:text-primary"
                  onClick={handleNextMonth} // Trigger next month
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>

              {/* Calendar Grid */}
              {/* Calendar Grid */}
              <div className="mb-4">
                {/* Weekday headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                    (day) => (
                      <div
                        key={day}
                        className="text-center text-xs font-medium text-gray-500 dark:text-muted-foreground py-2"
                      >
                        {day}
                      </div>
                    )
                  )}
                </div>

                {/* Calendar days */}
                <div className="grid grid-cols-7 gap-2">
                  {(() => {
                    const year = date.getFullYear();
                    const month = date.getMonth();
                    const firstDay = new Date(year, month, 1).getDay();
                    const daysInMonth = new Date(year, month + 1, 0).getDate();

                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    const days = [];

                    /* Empty cells before month start */
                    for (let i = 0; i < firstDay; i++) {
                      days.push(<div key={`empty-${i}`} />);
                    }

                    for (let day = 1; day <= daysInMonth; day++) {
                      const currentDate = new Date(year, month, day);
                      currentDate.setHours(0, 0, 0, 0);

                      const isPast = currentDate < today;
                      const isToday = currentDate.getTime() === today.getTime();

                      const dateStr = `${year}-${String(month + 1).padStart(
                        2,
                        "0"
                      )}-${String(day).padStart(2, "0")}`;

                      const dayData = availabilityData?.[dateStr];

                      /* Base styles */
                      let borderClass =
                        theme === "dark"
                          ? "border border-border"
                          : "border border-[#E8DDD3]";
                      let bgClass =
                        theme === "dark" ? "bg-secondary/20" : "bg-[#F4F0ED]";
                      let textClass =
                        theme === "dark" ? "text-foreground" : "text-[#4A4036]";
                      let hoverClass =
                        "hover:bg-[#F0E4C8] dark:hover:bg-primary/30 hover:text-black dark:hover:text-primary hover:shadow-lg hover:scale-110";

                      let cursorClass = "cursor-pointer";
                      let disabled = false;

                      /* Past dates */
                      if (isPast) {
                        bgClass =
                          theme === "dark" ? "bg-secondary/10" : "bg-[#F1F1F1]";
                        textClass =
                          theme === "dark"
                            ? "text-muted-foreground/40"
                            : "text-[#BDBDBD]";
                        borderClass = "border border-transparent";
                        // 🔥 hover ENABLED for past
                      }

                      /* Unavailable */
                      if (dayData && !dayData.available && !isPast) {
                        bgClass =
                          theme === "dark" ? "bg-destructive/20" : "bg-red-100";
                        textClass =
                          theme === "dark"
                            ? "text-destructive"
                            : "text-red-600";
                        borderClass =
                          theme === "dark"
                            ? "border border-destructive/50"
                            : "border border-red-300";
                        hoverClass = "";
                        cursorClass = "cursor-not-allowed";
                        disabled = true;
                      }

                      if (dayData?.projectAssigned && !isPast) {
                        bgClass = "bg-[#FFEBCC] dark:bg-orange-950/40"; // Light orange background
                        borderClass =
                          "border-2 border-orange-500 dark:border-orange-600/50"; // Orange border
                        textClass = "text-orange-900 dark:text-orange-200";
                        hoverClass = "dark:hover:bg-orange-900/60"; // Subtle hover
                        cursorClass = "cursor-pointer";
                      }

                      /* Today */
                      if (isToday) {
                        bgClass = "bg-white dark:bg-primary";
                        borderClass =
                          "border-2 border-black dark:border-primary";
                        textClass = "text-black dark:text-primary-foreground";
                      }
                      days.push(
                        <button
                          key={day}
                          disabled={disabled}
                          className={`
            w-[32px] h-[32px]
            rounded-xl
            flex items-center justify-center
            text-sm font-medium
            transition-all duration-200
            ${bgClass}
            ${textClass}
            ${borderClass}
            ${hoverClass}
            ${cursorClass}
          `}
                        >
                          {day}
                        </button>
                      );
                    }

                    return days;
                  })()}
                </div>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-3 text-xs text-gray-600 dark:text-muted-foreground mt-4">
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-rose-200 dark:bg-rose-900 border border-rose-300 dark:border-rose-800"></span>
                  <span>Unavailable</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-orange-200 dark:bg-orange-900 border border-orange-300 dark:border-orange-800"></span>
                  <span>Shoot</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-blue-200 dark:bg-blue-900 border border-blue-300 dark:border-blue-800"></span>
                  <span>Equipment</span>
                </div>
              </div>
            </div>
          </div>

          <Button
            className="w-full mt-6 bg-white dark:bg-card text-gray-900 dark:text-foreground border border-[#E8DDD3] dark:border-border hover:bg-[#C4B5A3] dark:hover:bg-primary hover:text-white dark:hover:text-primary-foreground hover:border-transparent shadow-sm transition-colors"
            onClick={() => navigate("/crew-availability")} // Handle the click event and redirect
          >
            Go to Availability
          </Button>
        </div>
      </div>

      {/* Upcoming Shoots Table Section */}
      <div className="bg-[#F5F1EE] dark:bg-secondary/10 rounded-2xl border border-[#E8DDD3] dark:border-border overflow-hidden">
        <div className="sticky top-0 z-10 bg-[#F5F1EE] dark:bg-card p-4 lg:p-6 pb-4 border-b border-[#E8DDD3] dark:border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-foreground">
              All Shoots
            </h2>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            {/* SEARCH INPUT FOR ALL SHOOTS */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-muted-foreground" />
              <Input
                placeholder="Search events or projects..."
                className="pl-10 bg-white dark:bg-background border-none shadow-sm h-10 text-gray-900 dark:text-foreground"
                value={shootsSearchQuery} // Add this
                onChange={(e) => setShootsSearchQuery(e.target.value)} // Add this
              />
            </div>

            <div className="flex gap-2">
              {/* STATUS SELECT */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[120px] bg-white dark:bg-background dark:border-muted-foreground border-none shadow-sm h-10 text-gray-900 dark:text-foreground">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-background border-none shadow-sm">
                  <SelectItem
                    value="all"
                    className="text-gray-900 dark:text-foreground"
                  >
                    All Status
                  </SelectItem>
                  <SelectItem
                    value="active"
                    className="text-gray-900 dark:text-foreground"
                  >
                    Active
                  </SelectItem>
                  <SelectItem
                    value="completed"
                    className="text-gray-900 dark:text-foreground"
                  >
                    Completed
                  </SelectItem>
                  <SelectItem
                    value="cancelled"
                    className="text-gray-900 dark:text-foreground"
                  >
                    Cancelled
                  </SelectItem>
                </SelectContent>
              </Select>

              {/* DATE FILTER SELECT */}
              <Select
                value={dateFilter}
                onValueChange={(val) => {
                  setDateFilter(val);
                  if (val === "custom") {
                    setIsDatePickerOpen(true);
                  }
                }}
              >
                <SelectTrigger className="w-[130px] bg-white dark:bg-background dark:border-muted-foreground border-none shadow-sm h-10 text-gray-900 dark:text-foreground">
                  <SelectValue placeholder="By Date" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-background border-none shadow-sm">
                  <SelectItem
                    value="all"
                    className="text-gray-900 dark:text-foreground"
                  >
                    All Dates
                  </SelectItem>
                  <SelectItem
                    value="today"
                    className="text-gray-900 dark:text-foreground"
                  >
                    Today
                  </SelectItem>
                  <SelectItem
                    value="upcoming"
                    className="text-gray-900 dark:text-foreground"
                  >
                    Upcoming
                  </SelectItem>
                  <SelectItem
                    value="this_week"
                    className="text-gray-900 dark:text-foreground"
                  >
                    This Week
                  </SelectItem>
                  <SelectItem
                    value="this_month"
                    className="text-gray-900 dark:text-foreground"
                  >
                    This Month
                  </SelectItem>
                  <SelectItem
                    value="custom"
                    className="text-gray-900 dark:text-foreground"
                  >
                    Custom
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Show active custom range if applicable */}
          {dateFilter === "custom" && customRange.from && customRange.to && (
            <div className="mt-2 text-xs text-gray-500">
              Filtered: {format(customRange.from, "PP")} -{" "}
              {format(customRange.to, "PP")}
              <button
                className="ml-2 text-blue-500 underline"
                onClick={() => setIsDatePickerOpen(true)}
              >
                Change
              </button>
            </div>
          )}
        </div>

        {/* Scrollable Content */}
        <div className="p-2 lg:p-6 lg:pt-0 max-h-[600px] overflow-y-auto no-scrollbar">
          {/* Event Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 lg:mt-6">
            {isLoading ? (
              <div className="col-span-2 text-center py-8 text-gray-500">
                Loading...
              </div>
            ) : filteredUpcomingProjects.length > 0 ? (
              filteredUpcomingProjects.map((event) => (
                <div
                  key={event.project_id}
                  className={cn(
                    cardHoverClass,
                    "bg-white dark:bg-card border-[#E8DDD3] dark:border-border shadow-sm hover:border-[#e8d0aa] dark:hover:border-primary/50"
                  )}
                >
                  <div className="flex justify-between items-start mb-1 lg:mb-3">
                    <h3 className="font-bold text-base text-gray-900 dark:text-foreground flex-1 pr-3">
                      {event.project.project_name}
                    </h3>
                    <div className="flex -space-x-2">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className="w-7 h-7 rounded-full border-2 border-white dark:border-card bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center overflow-hidden"
                        >
                          <img
                            src={`https://i.pravatar.cc/100?img=${10 + i}`}
                            alt="avatar"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                      <div className="w-7 h-7 rounded-full border-2 border-white dark:border-card bg-gray-100 dark:bg-background flex items-center justify-center text-xs font-medium text-gray-600 dark:text-muted-foreground">
                        +3
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4 text-gray-500 dark:text-muted-foreground text-sm">
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      <span>{event.project.event_date || "Date TBD"}</span>
                      <MapPin className="h-4 w-4 ml-2" />
                      <span>
                        {formatDisplayLocation(event.project.event_location) ||
                          "Location TBD"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>
                        {event.project.start_time && event.project.end_time
                          ? `${event.project.start_time} - ${event.project.end_time}`
                          : "Time Not Provided"}
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-3 border-t border-gray-100 dark:border-border">
                    <span className="text-gray-400 dark:text-muted-foreground font-medium text-sm">
                      Upcoming Shoot
                    </span>
                    <Button
                      className="bg-gray-900 dark:bg-primary text-white dark:text-primary-foreground hover:bg-gray-800 dark:hover:bg-primary/90 rounded-lg px-5 h-9 text-sm"
                      onClick={() =>
                        handleOpenProjectDetails(
                          event.project.stream_project_booking_id
                        )
                      }
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-2 text-center py-8 text-gray-500">
                {noShootsText}
              </div>
            )}
          </div>
        </div>
      </div>
      <Dialog open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
        <DialogContent className="sm:max-w-[400px] p-6">
          <h3 className="text-lg font-bold mb-4">Select Date Range</h3>
          <div className="flex justify-center border rounded-md p-2 bg-white">
            <Calendar
              initialFocus
              mode="range"
              selected={customRange}
              onSelect={setCustomRange}
              numberOfMonths={1}
            />
          </div>
          <div className="flex gap-3 mt-6">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setIsDatePickerOpen(false);
                setDateFilter("upcoming"); // Reset if cancel
              }}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-gray-900 text-white"
              onClick={handleApplyCustomDate}
            >
              Apply Filter
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <EventDetailsModal
        event={selectedEvent}
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
      />
      <ProjectDetailsModal
        open={projectDetailsOpen}
        onOpenChange={setProjectDetailsOpen}
        project={projectDetailsData}
      />

      {/* Shoot & Equipment Requests Section */}
      <div className="mt-4 lg:mt-8">
        {/* Parent Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Shoot Requests Section (takes 9 columns) */}
          <div className="col-span-12 lg:col-span-8">
            <div className="bg-[#F5F1EE] dark:bg-secondary/10 rounded-2xl border border-[#E8DDD3] dark:border-border overflow-hidden">
              {/* Sticky Header */}
              <div className="sticky top-0 z-10 bg-[#F5F1EE] dark:bg-card p-4 lg:p-6 pb-4 border-b border-[#E8DDD3] dark:border-border">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-foreground">
                    Shoot Requests
                  </h2>
                  <Button
                    variant="ghost"
                    className="text-[#A59479] dark:text-muted-foreground font-semibold hover:text-gray-900 dark:hover:text-foreground"
                  >
                    View all
                  </Button>
                </div>

                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-muted-foreground" />
                    <Input
                      placeholder="Search requests..."
                      className="pl-10 bg-white dark:bg-background border-none shadow-sm h-10 text-gray-900 dark:text-foreground"
                      value={requestsSearchQuery} // Add this
                      onChange={(e) => setRequestsSearchQuery(e.target.value)} // Add this
                    />
                  </div>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="p-2 lg:p-6 lg:pt-0 max-h-[600px] overflow-y-auto no-scrollbar">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  {isLoading ? (
                    <div className="text-center py-8 text-gray-500">
                      Loading...
                    </div>
                  ) : filteredPendingProjects.length > 0 ? (
                    filteredPendingProjects.map((req) => (
                      <div
                        key={req.project_id}
                        className={cn(
                          cardHoverClass,
                          "bg-white dark:bg-card border-[#E8DDD3] dark:border-border shadow-sm hover:border-[#e8d0aa] dark:hover:border-primary/50"
                        )}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="font-bold text-base text-gray-900 dark:text-foreground flex-1 pr-3">
                            {req.project_name}
                          </h3>
                          <div className="flex -space-x-2">
                            {[1, 2, 3, 4].map((i) => (
                              <div
                                key={i}
                                className="w-7 h-7 rounded-full border-2 border-white dark:border-card bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center overflow-hidden"
                              >
                                <img
                                  src={`https://i.pravatar.cc/100?img=${
                                    10 + i
                                  }`}
                                  alt="avatar"
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ))}
                            <div className="w-7 h-7 rounded-full border-2 border-white dark:border-card bg-gray-100 dark:bg-background flex items-center justify-center text-xs font-medium text-gray-600 dark:text-muted-foreground">
                              +3
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2 mb-4 text-gray-500 dark:text-muted-foreground text-sm">
                          <div className="flex items-center gap-2">
                            <CalendarIcon className="h-4 w-4" />
                            <span>{req.event_date || "Date TBD"}</span>
                            <MapPin className="h-4 w-4 ml-2" />
                            <span>
                              {req.display_location || "Location TBD"}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            <span>
                              {req.start_time && req.end_time
                                ? `${req.start_time} - ${req.end_time}`
                                : "Time not Provided"}
                            </span>
                          </div>
                        </div>

                        <div className="flex justify-between items-center pt-3 border-t border-gray-100 dark:border-border">
                          <span className="text-gray-400 dark:text-muted-foreground font-medium text-sm">
                            Shoot Request
                            {/* {format(req?.assigned_date, "yyyy-mm-dd")} */}
                          </span>
                          <div className="flex gap-2">
                            <Button
                              className="bg-gray-900 dark:bg-primary text-white dark:text-primary-foreground hover:bg-gray-800 dark:hover:bg-primary/90 rounded-lg px-4 h-9 text-sm"
                              onClick={() =>
                                handleOpenProjectDetails(req.project_id)
                              }
                            >
                              View Details
                            </Button>
                            <Button
                              variant="outline"
                              className="border-2 border-emerald-500 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 h-9 w-9 p-0 rounded-lg"
                              onClick={() => setAcceptShootEvent(req)}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              className="border-2 border-red-500 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 h-9 w-9 p-0 rounded-lg"
                              onClick={() =>
                                handleAcceptProject(req.project_id, 2)
                              }
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No pending requests
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity Section (takes 3 columns) */}
          <div className="col-span-12 lg:col-span-4">
            <div className="bg-white dark:bg-secondary/10 rounded-2xl border border-[#E8DDD3] dark:border-border flex flex-col overflow-hidden h-[400px]">
              {/* Header */}
              <div className="p-6 pb-4">
                <h2 className="text-lg font-bold text-gray-900 dark:text-foreground">
                  Recent Activity
                </h2>
              </div>

              {/* Activity Content - Changed 'h-full' to 'flex-1' */}
              <div className="px-6 flex-1 overflow-y-auto no-scrollbar">
                {isLoading ? (
                  <div className="text-center py-8 text-gray-500">
                    Loading...
                  </div>
                ) : error ? (
                  <div className="text-center py-8 text-gray-500">{error}</div>
                ) : recentActivity.length > 0 ? (
                  /* Added pb-10 here to ensure the last item has breathing room at the bottom */
                  <div className="relative pb-10">
                    {recentActivity.map((activity, idx) => {
                      const isLast = idx === recentActivity.length - 1;

                      // Format to "X min ago"
                      // const timeAgo = formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true });

                      return (
                        <div
                          key={idx}
                          className="relative flex gap-4 pb-8 group"
                        >
                          {/* Vertical Line Connector */}
                          {!isLast && (
                            <span
                              className="absolute left-[18px] top-10 w-[1px] h-full bg-gray-200 dark:bg-gray-800"
                              aria-hidden="true"
                            />
                          )}

                          {/* Avatar / Icon Container */}
                          <div className="relative flex-shrink-0">
                            <div className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden">
                              {getIcon(
                                activity.type,
                                activity.userImage,
                                activity.initials
                              )}
                            </div>
                          </div>

                          {/* Text Content */}
                          <div className="flex flex-col gap-1 pt-1">
                            <h3 className="text-[15px] font-bold text-gray-900 dark:text-foreground leading-tight">
                              {activity.title}
                            </h3>
                            <p className="text-[14px] text-gray-600 dark:text-gray-400 leading-relaxed max-w-[280px]">
                              {activity.description}
                            </p>
                            {/* <div className="flex items-center gap-1.5 mt-1 text-gray-400 dark:text-muted-foreground">
                    <Clock size={14} />
                    <span className="text-xs font-medium">{timeAgo}</span>
                  </div> */}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No recent activity
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Accept Shoot Modal - Slides from Right */}
      <Dialog
        open={!!acceptShootEvent}
        onOpenChange={() => setAcceptShootEvent(null)}
      >
        <DialogContent className=" fixed z-50 left-[50%] top-[50%] w-full max-w-xs md:max-w-md translate-x-[-50%] translate-y-[-50%] p-6 bg-white dark:bg-card shadow-xl rounded-xl border border-gray-100 dark:border-border">
          <div className="flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center mb-4">
              <Check className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
            </div>

            <h2 className="text-xl font-bold text-gray-900 dark:text-foreground mb-2">
              Accept this shoot request?
            </h2>
            <p className="text-gray-500 dark:text-muted-foreground text-sm mb-6">
              Project:{" "}
              <span className="font-semibold text-gray-700 dark:text-foreground">
                {acceptShootEvent?.project_name}
              </span>
              <br />
              This will notify the production team and add the event to your
              schedule.
            </p>

            <div className="flex gap-3 w-full">
              <Button
                variant="outline"
                className="flex-1 bg-gray-100 dark:bg-background border-none text-gray-900 dark:text-foreground hover:bg-gray-200 dark:hover:bg-gray-800"
                onClick={() => setAcceptShootEvent(null)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white gap-2"
                onClick={() => {
                  if (acceptShootEvent) {
                    // Passes the ID as first param, and 1 (Accept) as second param
                    handleAcceptProject(acceptShootEvent.project_id, 1);
                  }
                }}
              >
                <Check className="h-4 w-4" /> Confirm & Accept
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Decline Equipment Modal - Slides from Bottom */}
      <Dialog
        open={!!declineEquipmentItem}
        onOpenChange={() => setDeclineEquipmentItem(null)}
      >
        <DialogContent className="fixed z-50 left-[50%] bottom-0 top-auto w-full max-w-lg translate-x-[-50%] translate-y-0 p-6 bg-white dark:bg-card shadow-xl rounded-t-2xl sm:rounded-xl sm:bottom-auto sm:top-[50%] sm:translate-y-[-50%] border border-gray-100 dark:border-border data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-500 dark:text-red-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-foreground">
                  Decline Equipment Request?
                </h2>
              </div>
            </div>
          </div>

          <p className="text-gray-500 dark:text-muted-foreground text-sm mb-6">
            Please let us know why you're declining this equipment request. Your
            feedback helps us improve future matching.
          </p>

          <div className="mb-6">
            <h3 className="font-bold text-gray-900 dark:text-foreground mb-3 block">
              Reason for declining
            </h3>
            <div className="space-y-3">
              {[
                "Schedule conflict",
                "Equipment unavailable",
                "Location too far",
                "Rate too low",
                "Other",
              ].map((reason) => (
                <div key={reason} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="decline-reason"
                    id={reason}
                    className="h-4 w-4 text-gray-900 dark:text-foreground border-gray-300 dark:border-border focus:ring-gray-900 dark:focus:ring-foreground"
                  />
                  <Label
                    htmlFor={reason}
                    className="text-gray-600 dark:text-muted-foreground font-normal cursor-pointer"
                  >
                    {reason}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <h3 className="font-bold text-gray-900 dark:text-foreground mb-3 block">
              Additional comments (optional)
            </h3>
            <Textarea
              placeholder="Any additional details.."
              className="min-h-[80px] bg-white dark:bg-background border-gray-200 dark:border-border text-gray-900 dark:text-foreground"
            />
          </div>

          <div className="flex gap-3 w-full">
            <Button
              variant="outline"
              className="flex-1 bg-gray-100 dark:bg-background border-none text-gray-900 dark:text-foreground hover:bg-gray-200 dark:hover:bg-gray-800"
              onClick={() => setDeclineEquipmentItem(null)}
            >
              Cancel
            </Button>
            <Button className="flex-1 bg-red-500 hover:bg-red-600 text-white gap-2">
              <Check className="h-4 w-4" /> Decline Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreatorDashboard;
